import { useContext, useMemo } from 'react';
import {
  AppConfigurationsContext,
  AuthContext,
} from '@investigator/shared/providers';
import { hasChatGPTPrivilege } from '../utils/checkChatGPTPrivilege';

/**
 * Settings for Agentic Triage features
 *
 * These are admin-configured settings that are part of the chatgpt_private_data
 * integration. They control AI-powered triage features.
 */
export interface AgenticTriageSettings {
  /**
   * Whether the Agentic Triage feature flag is enabled
   * True only if:
   * - AWS feature flag `agentic_triage` is enabled
   *
   * This gates the overall availability of AI triage features.
   * The sub-setting (agenticTriageEnabled) cannot be true unless this is true.
   */
  agenticTriageFeatureEnabled: boolean;

  /**
   * Whether Agentic Triage is enabled for the user
   * True only if:
   * - Feature flag is enabled (agenticTriageFeatureEnabled = true)
   * - User has `chatgpt_private_data` privilege
   *
   * This controls all agentic features including triage and session summaries.
   */
  agenticTriageEnabled: boolean;
}

/**
 * Hook to access Agentic Triage settings
 *
 * This hook combines AWS feature flags and user privileges to determine
 * which Agentic Triage features are available and enabled.
 *
 * Requirements for agenticTriageEnabled:
 * 1. AWS feature flag `agentic_triage` must be enabled
 * 2. User must have `chatgpt_private_data` privilege
 *
 * Cross-tab synchronization:
 * Settings automatically sync across tabs via the existing AuthProvider
 * privilege synchronization. No additional setup needed.
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { agenticTriageEnabled } = useAgenticTriageSettings();
 *   if (!agenticTriageEnabled) return null;
 *   return <AIFeature />;
 * }
 * ```
 *
 * @returns {AgenticTriageSettings} Current agentic triage settings
 */
export const useAgenticTriageSettings: () => AgenticTriageSettings = () => {
  const { appConfigurations } = useContext(AppConfigurationsContext);
  const { userLicense, userPrivileges } = useContext(AuthContext);

  return useMemo(() => {
    const currentTenant = userLicense?.tenant;
    const tenantPrivileges = currentTenant
      ? userPrivileges?.[currentTenant]
      : null;

    // Check if the AWS feature flag is enabled
    const agenticTriageFeatureFlag =
      appConfigurations?.feature_flags?.agentic_triage === true;

    // Check if user has the base privilege
    const hasChatGPTPrivateDataPrivilege = hasChatGPTPrivilege(
      tenantPrivileges ?? undefined,
    );

    // Feature is available if flag is enabled
    const agenticTriageFeatureEnabled = agenticTriageFeatureFlag;

    // Enabled if feature flag is on AND user has chatGPT integration
    const agenticTriageEnabled =
      agenticTriageFeatureEnabled && hasChatGPTPrivateDataPrivilege;

    return {
      agenticTriageFeatureEnabled,
      agenticTriageEnabled,
    };
  }, [appConfigurations, userLicense, userPrivileges]);
};
